import { useRef } from "react";
import { motion, useMotionValue, useReducedMotion, useScroll, useSpring, useTransform } from "framer-motion";
import { LogoMark } from "./Logo";

export const ArchPhoto = ({ src, alt, accent = "#B4532A", label, testId = "hero-arch-photo" }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "14%"]);

  const onMove = (e) => {
    if (reduce) return;
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  return (
    <div ref={ref} className="relative [perspective:1200px]" onMouseMove={onMove} onMouseLeave={onLeave} data-testid={testId}>
      <motion.div
        initial={{ opacity: 0, y: 60, scale: 0.94 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.35 }}
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        className="relative mx-auto w-full max-w-[460px]"
      >
        <div className="absolute -inset-4 sm:-inset-6 rounded-t-[999px] rounded-b-[2.5rem] bg-[#B4532A]/[0.07] -rotate-2" />
        <div className="relative aspect-[4/5] overflow-hidden rounded-t-[999px] rounded-b-[2rem] border border-[#E8DDD3] shadow-[0_40px_80px_-30px_rgba(31,26,23,0.45)]">
          <motion.img src={src} alt={alt} style={{ y: imgY, scale: 1.18 }} className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#181412]/55 via-transparent to-transparent" />
          <div className="absolute inset-0 hv-spotlight" />
        </div>
        <div
          style={{ transform: "translateZ(60px)" }}
          className="absolute -left-3 sm:-left-10 bottom-10 rounded-2xl bg-white/90 backdrop-blur-md border border-[#E8DDD3] px-4 py-3 shadow-xl flex items-center gap-3"
        >
          <span className="h-9 w-9 rounded-xl grid place-items-center text-white" style={{ background: accent }}>
            <LogoMark className="h-4 w-4" />
          </span>
          <span className="leading-tight">
            <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#685F58]">Vault</span>
            <span className="block text-sm font-medium text-[#1F1A17]">{label}</span>
          </span>
        </div>
      </motion.div>
    </div>
  );
};
